import { useCallback, useEffect, useRef } from "react";

interface SectionMeta<T extends string> {
  value: T;
  /** Registration order (first `registerSection(value)` call wins the slot) — used to break ties, not `intersectionRatio` (see below). */
  order: number;
  isIntersecting: boolean;
}

/**
 * A ref callback factory: `registerSection("Income")` returns a callback ref
 * to attach to a section's DOM node. Multiple nodes (e.g. one per signer
 * column) can share the same `value` — the factory caches and returns the
 * SAME callback for a given value, so passing it inline as `ref={...}` never
 * churns the ref across unrelated re-renders (e.g. a sibling field's keystroke).
 */
export type SectionRegistrar<T extends string> = (value: T) => (element: HTMLElement | null) => (() => void) | void;

// Narrow horizontal band roughly a third of the way down the viewport — a
// section only counts as "active" once it has scrolled up into this band,
// matching typical scrollspy behavior (the heading you're "at" is the one
// just below this line, not the one merely peeking into view at the top).
const ACTIVATION_ROOT_MARGIN = "-30% 0px -55% 0px";

/**
 * Tracks a set of registered section elements and reports whichever is most
 * relevant within the activation band as the user scrolls. Sections may mount
 * and unmount freely (e.g. a collapsed subsection) — `registerSection`'s
 * cleanup function deregisters them.
 * @template T
 * @param onActiveChange - Called with the active section's value, only when it actually changes.
 * @returns {SectionRegistrar<T>} A ref-callback factory to attach to each candidate section element.
 */
export function useScrollSpy<T extends string>(onActiveChange: (value: T) => void): SectionRegistrar<T> {
  const sectionsReference = useRef(new Map<Element, SectionMeta<T>>());
  // One stable ref-callback per distinct `value` (e.g. "Income"), reused across
  // every element registered under it (both signer columns) and across every
  // re-render — so passing `registerSection("Income")` inline as a JSX `ref`
  // never triggers a spurious detach/reattach on an unrelated re-render.
  const registrarsReference = useRef(new Map<T, (element: HTMLElement | null) => (() => void) | void>());
  const orderCounterReference = useRef(0);
  const lastActiveReference = useRef<T | null>(null);
  const onActiveChangeReference = useRef(onActiveChange);
  onActiveChangeReference.current = onActiveChange;
  const observerReference = useRef<IntersectionObserver | null>(null);

  useEffect(() => () => observerReference.current?.disconnect(), []);

  return useCallback((value: T) => {
    const cached = registrarsReference.current.get(value);
    if (cached) {
      return cached;
    }

    // Order reflects registration sequence, which for a single signer column
    // matches document order (Income, then Bank, then Residence). When two
    // sections straddle the activation band at once, the later one wins —
    // NOT the one with the higher `intersectionRatio`, which would unfairly
    // favor a short section over a tall one occupying the same thin band.
    const order = orderCounterReference.current++;

    const registrar = (element: HTMLElement | null) => {
      if (!element) {
        return;
      }

      // Created lazily on the first section's actual DOM commit (here, inside
      // the ref callback) rather than during render, so a render that never
      // commits (e.g. an interrupted concurrent render) never allocates one.
      if (observerReference.current === null && typeof IntersectionObserver !== "undefined") {
        observerReference.current = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const meta = sectionsReference.current.get(entry.target);
              if (!meta) {
                continue;
              }
              meta.isIntersecting = entry.isIntersecting;
            }
            let active: SectionMeta<T> | undefined;
            for (const meta of sectionsReference.current.values()) {
              if (meta.isIntersecting && (!active || meta.order > active.order)) {
                active = meta;
              }
            }
            if (active && active.value !== lastActiveReference.current) {
              lastActiveReference.current = active.value;
              onActiveChangeReference.current(active.value);
            }
          },
          { rootMargin: ACTIVATION_ROOT_MARGIN }
        );
      }

      sectionsReference.current.set(element, { value, order, isIntersecting: false });
      observerReference.current?.observe(element);
      return () => {
        sectionsReference.current.delete(element);
        observerReference.current?.unobserve(element);
      };
    };

    registrarsReference.current.set(value, registrar);
    return registrar;
  }, []);
}
